// src/components/admin/PromotionsEditor.jsx
// PART 1 OF 2: Imports, CustomerPreviewModal Component, and Helper Functions

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getSalonPromotions,
  addSalonPromotion,
  updateSalonPromotion,
  deleteSalonPromotion,
  getCustomerTargetingPreview,
  pushVouchersToCustomers,
  getSalonServices,
  getSalonStaff
} from '../../api/salonApi';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  limit 
} from 'firebase/firestore';
import { db } from '../../utils/firebase';
import '../../styles/promotions.css';
import '../../styles/customer-preview-modal.css';
import { useSalonLocations } from '../../hooks/useSalonLocations';

// Customer Preview Modal Component
function CustomerPreviewModal({ isOpen, onClose, previewData, promotionName }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [filterText, setFilterText] = useState('');
  const [sortField, setSortField] = useState('displayName');
  const [sortDirection, setSortDirection] = useState('asc');
  const customersPerPage = 100;

  useEffect(() => {
    setCurrentPage(1);
  }, [filterText]);

  if (!isOpen || !previewData) return null;

  const { eligibleCustomers = [], eligibleCount = 0, totalCount = 0 } = previewData;

  const filteredCustomers = eligibleCustomers.filter(customer => {
    if (!filterText) return true;
    const searchLower = filterText.toLowerCase();
    return (
      customer.displayName?.toLowerCase().includes(searchLower) ||
      customer.email?.toLowerCase().includes(searchLower)
    );
  });

  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    const aVal = a[sortField] || '';
    const bVal = b[sortField] || '';
    
    if (sortDirection === 'asc') {
      return aVal.localeCompare(bVal);
    } else {
      return bVal.localeCompare(aVal);
    }
  });

  const totalPages = Math.ceil(sortedCustomers.length / customersPerPage);
  const startIndex = (currentPage - 1) * customersPerPage;
  const endIndex = startIndex + customersPerPage;
  const currentCustomers = sortedCustomers.slice(startIndex, endIndex);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExportCSV = () => {
    const headers = ['Name', 'Email'];
    const csvRows = [headers.join(',')];

    sortedCustomers.forEach(customer => {
      const row = [
        `"${customer.displayName || ''}"`,
        `"${customer.email || ''}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const date = new Date().toISOString().split('T')[0];
    const filename = `eligible-customers-${promotionName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${date}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const coverage = totalCount > 0 ? ((eligibleCount / totalCount) * 100).toFixed(1) : '0.0';

  return (
    <div className="modal-overlay">
      <div className="customer-preview-modal">
        <div className="modal-header">
          <h3>Target Customer Preview</h3>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-summary">
          <div className="summary-item">
            <span className="summary-label">Eligible Customers</span>
            <span className="summary-value">{eligibleCount}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Total Customers</span>
            <span className="summary-value">{totalCount}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Coverage</span>
            <span className="summary-value coverage">{coverage}%</span>
          </div>
        </div>

        <div className="modal-body">
          {eligibleCount > 0 ? (
            <>
              <button 
                className="export-csv-btn" 
                onClick={handleExportCSV}
                disabled={sortedCustomers.length === 0}
              >
                📥 Export to CSV
              </button>

              <div className="table-filter">
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
                {filterText && (
                  <button 
                    className="filter-clear-btn"
                    onClick={() => setFilterText('')}
                  >
                    Clear
                  </button>
                )}
              </div>

              {currentCustomers.length > 0 ? (
                <>
                  <div className="customer-table-container">
                    <table className="customer-table">
                      <thead>
                        <tr>
                          <th 
                            onClick={() => handleSort('displayName')}
                            className={sortField === 'displayName' ? `sorted-${sortDirection}` : ''}
                          >
                            Customer Name
                            <span className="sort-indicator"></span>
                          </th>
                          <th 
                            onClick={() => handleSort('email')}
                            className={sortField === 'email' ? `sorted-${sortDirection}` : ''}
                          >
                            Email Address
                            <span className="sort-indicator"></span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentCustomers.map((customer, index) => (
                          <tr key={customer.id || index}>
                            <td>{customer.displayName || 'N/A'}</td>
                            <td>{customer.email || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      
                      <span className="pagination-info">
                        Page {currentPage} of {totalPages}
                      </span>
                      
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">🔍</div>
                  <p className="empty-state-text">No customers match your search</p>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">👥</div>
              <p className="empty-state-text">No eligible customers found for the selected criteria</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PromotionsEditor({ salonId, onUpdate, onCreateCampaign }) {
  const location = useLocation();
  const { locations } = useSalonLocations(salonId);
  const isMultiLocation = locations.length > 1;
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [isEditing, setIsEditing] = useState(false);
  const [editingPromotionId, setEditingPromotionId] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [selectedPromotions, setSelectedPromotions] = useState([]);
  
  // NEW: State for tracking if targeting criteria has been saved
  const [targetingCriteriaSaved, setTargetingCriteriaSaved] = useState(false);

  // State for tracking if preview has been completed and voucher push
  const [previewCompleted, setPreviewCompleted] = useState(false);
  const [pushingVouchers, setPushingVouchers] = useState(false);
  
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  
  // UPDATED: Default values with "not_selected" options
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'percentage',
    discountValue: 0,
    freeGiftDescription: '',
    pointsRequired: 0,
    voucherAmount: 0,
    voucherCode: '',
    startDate: '',
    endDate: '',
    targetingType: 'not_selected',
    targetingCriteria: {
      birthMonths: [],
      postalCodes: [],
      lastTransactionMonths: 0,
      transactionCount: 0,
      transactionType: 'not_selected',
      transactionAmountType: 'not_selected',
      transactionAmount: 0,
      transactionLocationId: 'not_selected',
      memberTiers: [],
      pointsType: 'not_selected',
      pointsAmount: 0,
      specificCustomers: [],
      notesSearchText: '', // #Revised UI_v2 — customer notes targeting
      notesMatchType: 'contains_all', // #Revised UI_v2 — customer notes targeting
      appointmentTimeValue: 0,
      appointmentTimeUnit: 'days',
      appointmentService: 'not_selected',
      appointmentStaff: 'not_selected',
      appointmentStatus: []
    },
    isActive: true,
    maxRedemptions: null,
    currentRedemptions: 0
  });

  const [fieldErrors, setFieldErrors] = useState({
    name: '',
    type: '',
    discountValue: '',
    freeGiftDescription: '',
    pointsRequired: '',
    voucherAmount: '',
    startDate: '',
    endDate: '',
    targetingType: ''
  });

  const birthMonthOptions = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const memberTierOptions = [
    'Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond'
  ];

  // State for appointment-based targeting
  const [salonServices, setSalonServices] = useState([]);
  const [salonStaff, setSalonStaff] = useState([]);
  const [loadingServicesStaff, setLoadingServicesStaff] = useState(false);

  const appointmentStatusOptions = ['approved', 'pending', 'rejected', 'cancelled'];

  const searchCustomers = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setCustomerSearchResults([]);
      return;
    }

    setSearchingCustomers(true);
    try {
      const customersRef = collection(db, 'customers');
      const emailQuery = query(
        customersRef,
        where('salonId', '==', salonId),
        limit(10)
      );
      
      const emailSnapshot = await getDocs(emailQuery);
      const customers = [];
      
      emailSnapshot.forEach((doc) => {
        const customerData = doc.data();
        const customerId = doc.id;
        
        const emailMatch = customerData.email && customerData.email.toLowerCase().includes(searchTerm.toLowerCase());
        const nameMatch = customerData.displayName && customerData.displayName.toLowerCase().includes(searchTerm.toLowerCase());
        
        if (emailMatch || nameMatch) {
          customers.push({
            id: customerId,
            email: customerData.email,
            displayName: customerData.displayName,
            phoneNumber: customerData.phoneNumber
          });
        }
      });
      
      const uniqueCustomers = customers.filter((customer, index, self) => 
        index === self.findIndex(c => c.id === customer.id) &&
        !selectedCustomers.some(selected => selected.id === customer.id)
      );
      
      setCustomerSearchResults(uniqueCustomers);
    } catch (err) {
      console.error('Error searching customers:', err);
      setCustomerSearchResults([]);
    } finally {
      setSearchingCustomers(false);
    }
  };

  const handleCustomerSearchChange = (e) => {
    const value = e.target.value;
    setCustomerSearchQuery(value);
    setShowCustomerDropdown(true);
    
    const timeoutId = setTimeout(() => {
      searchCustomers(value);
    }, 300);
    
    return () => clearTimeout(timeoutId);
  };

  const handleCustomerSelect = (customer) => {
    const updatedCustomers = [...selectedCustomers, customer];
    setSelectedCustomers(updatedCustomers);
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
    setShowCustomerDropdown(false);
    
    handleTargetingCriteriaChange('specificCustomers', updatedCustomers);
  };

  const handleCustomerRemove = (customerId) => {
    const updatedCustomers = selectedCustomers.filter(customer => customer.id !== customerId);
    setSelectedCustomers(updatedCustomers);
    handleTargetingCriteriaChange('specificCustomers', updatedCustomers);
  };

  const generateEmailSubject = (promotionData) => {
    const { name, type, discountValue, freeGiftDescription, pointsRequired, voucherAmount } = promotionData;
    const subjects = {
      percentage: [`🎉 ${discountValue}% OFF Just For You - ${name}!`],
      fixed_amount: [`💰 Save $${discountValue} with ${name}!`],
      free_gift: [`🎁 Free Gift with ${name}!`],
      points_based: [`⭐ Redeem ${pointsRequired} Points for $${voucherAmount} - ${name}!`]
    };
    const typeSubjects = subjects[type] || subjects.percentage;
    return typeSubjects[0];
  };

  const generateEmailBody = (promotionData) => {
    const { name, description, type, discountValue } = promotionData;
    return `Dear Valued Customer,\n\n🌟 We're excited to share an exclusive offer on our ${name}!\n\n${description || 'Indulge in our premium beauty services with this limited-time offer.'}\n\nBook your appointment today!\n\nWith love,\nYour Beauty Team 💄`;
  };

  const generatePushSubject = (promotionData) => {
    const { type, discountValue } = promotionData;
    const subjects = {
      percentage: [`🔥 ${discountValue}% OFF Flash Sale!`],
      fixed_amount: [`💸 Save $${discountValue} Today Only!`],
      free_gift: [`🎁 Free Gift Alert!`],
      points_based: [`⭐ Points Redemption Available!`]
    };
    const typeSubjects = subjects[type] || subjects.percentage;
    return typeSubjects[0];
  };

  const generatePushBody = (promotionData) => {
    const { name } = promotionData;
    return `${name} - Limited time offer! Tap to book your appointment! 🌟`;
  };

  const handleCreateEmailCampaign = () => {
    if (!formData.name) {
      setError('Please save the promotion first before creating a campaign');
      return;
    }
    const emailSubject = generateEmailSubject(formData);
    const emailBody = generateEmailBody(formData);
    const notificationData = {
      type: 'email',
      subject: emailSubject,
      body: emailBody,
      targetingType: formData.targetingType,
      targetingCriteria: formData.targetingCriteria,
      promotionSource: {
        promotionName: formData.name,
        promotionType: formData.type,
        promotionId: editingPromotionId,
        voucherCode: formData.voucherCode
      }
    };
    sessionStorage.setItem('pendingNotificationData', JSON.stringify(notificationData));
    if (onCreateCampaign) {
      onCreateCampaign('communications', 'email');
    }
  };

  const handleCreatePushCampaign = () => {
    if (!formData.name) {
      setError('Please save the promotion first before creating a campaign');
      return;
    }
    const pushSubject = generatePushSubject(formData);
    const pushBody = generatePushBody(formData);
    const notificationData = {
      type: 'push',
      subject: pushSubject,
      body: pushBody,
      targetingType: formData.targetingType,
      targetingCriteria: formData.targetingCriteria,
      promotionSource: {
        promotionName: formData.name,
        promotionType: formData.type,
        promotionId: editingPromotionId,
        voucherCode: formData.voucherCode
      }
    };
    sessionStorage.setItem('pendingNotificationData', JSON.stringify(notificationData));
    if (onCreateCampaign) {
      onCreateCampaign('communications', 'push');
    }
  };
  
  useEffect(() => {
    const loadPromotions = async () => {
      try {
        setLoading(true);
        const loadedPromotions = await getSalonPromotions(salonId);
        setPromotions(loadedPromotions);
        setError('');
      } catch (err) {
        console.error('Error loading promotions:', err);
        setError('Failed to load promotions. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadPromotions();
  }, [salonId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.customer-search-container')) {
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load services and staff for appointment-based targeting
  useEffect(() => {
    const loadServicesAndStaff = async () => {
      if (!salonId) return;

      setLoadingServicesStaff(true);
      try {
        const [services, staff] = await Promise.all([
          getSalonServices(salonId),
          getSalonStaff(salonId)
        ]);

        setSalonServices(services.filter(s => s.isActive !== false));
        setSalonStaff(staff.filter(s => s.isActive !== false));
      } catch (err) {
        console.error('Error loading services and staff:', err);
      } finally {
        setLoadingServicesStaff(false);
      }
    };

    loadServicesAndStaff();
  }, [salonId]);

// NEW: Check for AI-populated parameters from sessionStorage
  useEffect(() => {
    const aiParameters = sessionStorage.getItem('aiPromotionParameters');
    const targetTab = sessionStorage.getItem('targetTab');
    
if (aiParameters && (targetTab === 'create-promotion' || targetTab === 'promotions' || sessionStorage.getItem('aiPromotionSource') === 'bespokeai')) {
      try {
        const params = JSON.parse(aiParameters);
        
        // Switch to "Add New Promotion" mode
        setIsEditing(true);
        setEditingPromotionId(null);
        
        // Pre-populate form with AI parameters
        setFormData({
          name: params.name || '',
          description: params.description || '',
          type: params.type || 'percentage',
          discountValue: params.discountValue || 0,
          freeGiftDescription: params.freeGiftDescription || '',
          pointsRequired: params.pointsRequired || 0,
          voucherAmount: params.voucherAmount || 0,
          voucherCode: params.voucherCode || '',
          startDate: params.startDate || '',
          endDate: params.endDate || '',
          targetingType: params.targetingType || 'not_selected',
          targetingCriteria: params.targetingCriteria || {
            birthMonths: [],
            postalCodes: [],
            lastTransactionMonths: 0,
            transactionCount: 0,
            transactionType: 'not_selected',
            transactionAmountType: 'not_selected',
            transactionAmount: 0,
            transactionLocationId: 'not_selected',
            memberTiers: [],
            pointsType: 'not_selected',
            pointsAmount: 0,
            specificCustomers: [],
            appointmentTimeValue: 0,
            appointmentTimeUnit: 'days',
            appointmentService: 'not_selected',
            appointmentStaff: 'not_selected',
            appointmentStatus: []
          },
          isActive: params.isActive !== undefined ? params.isActive : true,
          maxRedemptions: params.maxRedemptions || null,
          currentRedemptions: 0
        });
        
        // If specific customers were provided, set them
        if (params.targetingCriteria?.specificCustomers && 
            Array.isArray(params.targetingCriteria.specificCustomers)) {
          setSelectedCustomers(params.targetingCriteria.specificCustomers);
        }
        
        // Mark targeting criteria as saved if provided
        if (params.targetingType && params.targetingType !== 'not_selected') {
          setTargetingCriteriaSaved(true);
        }
        
        // Show success message
        setSuccessMessage('AI recommendations loaded successfully! Review and adjust as needed.');
        setTimeout(() => setSuccessMessage(''), 5000);
        
        // Clear sessionStorage after loading
        sessionStorage.removeItem('aiPromotionParameters');
        sessionStorage.removeItem('targetTab');
        sessionStorage.removeItem('aiPromotionSource');

      } catch (error) {
        console.error('Error loading AI parameters:', error);
        setError('Failed to load AI recommendations. Please try again.');
      }
    }
  }, [location]); // Re-run when location changes (e.g., navigation from AI page)

  const validateField = (fieldName, value, currentFormData = formData) => {
    let errorMsg = '';

    if (fieldName === 'name' && (!value || value.trim() === '')) {
      errorMsg = 'Promotion Name is mandatory. Please fill in this field.';
    } else if (fieldName === 'type' && !value) {
      errorMsg = 'Promotion Type is mandatory. Please select a type.';
    } else if (fieldName === 'discountValue' && (currentFormData.type === 'percentage' || currentFormData.type === 'fixed_amount')) {
      if (!value || Number(value) <= 0) {
        errorMsg = 'Discount value must be greater than 0.';
      } else if (currentFormData.type === 'percentage' && Number(value) > 100) {
        errorMsg = 'Percentage discount must be between 1 and 100.';
      }
    } else if (fieldName === 'freeGiftDescription' && currentFormData.type === 'free_gift') {
      if (!value || value.trim() === '') {
        errorMsg = 'Free Gift Description is mandatory. Please fill in this field.';
      }
    } else if (fieldName === 'pointsRequired' && currentFormData.type === 'points_based') {
      if (!value || Number(value) <= 0) {
        errorMsg = 'Points Required must be greater than 0.';
      }
    } else if (fieldName === 'voucherAmount' && currentFormData.type === 'points_based') {
      if (!value || Number(value) <= 0) {
        errorMsg = 'Voucher Amount must be greater than 0.';
      }
    } else if (fieldName === 'startDate' && (!value || value.trim() === '')) {
      errorMsg = 'Start Date is mandatory. Please select a date.';
    } else if (fieldName === 'endDate' && (!value || value.trim() === '')) {
      errorMsg = 'End Date is mandatory. Please select a date.';
    } else if (fieldName === 'targetingType' && (!value || value === 'not_selected')) {
      errorMsg = 'Target Audience is mandatory. Please select an option.';
    }

    setFieldErrors(prev => ({ ...prev, [fieldName]: errorMsg }));
    return errorMsg === '';
  };

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    if (name === 'name' || name === 'discountValue' || name === 'freeGiftDescription' ||
        name === 'pointsRequired' || name === 'voucherAmount' || name === 'startDate' ||
        name === 'endDate') {
      validateField(name, value);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // NEW: Reset targeting criteria saved state and clear criteria when targeting type changes
    if (name === 'targetingType') {
      setTargetingCriteriaSaved(false);
      setPreviewCompleted(false);
      setFormData({
        ...formData,
        [name]: value,
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          transactionLocationId: 'not_selected',
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          appointmentTimeValue: 0,
          appointmentTimeUnit: 'days',
          appointmentService: 'not_selected',
          appointmentStaff: 'not_selected',
          appointmentStatus: []
        }
      });
      setSelectedCustomers([]);
      return;
    }
    
    const newValue = type === 'checkbox' ? checked :
              name === 'discountValue' || name === 'maxRedemptions' || name === 'pointsRequired' || name === 'voucherAmount' ?
                (value === '' ? null : Number(value)) : value;

    const updatedFormData = {
      ...formData,
      [name]: newValue
    };

    setFormData(updatedFormData);

    // Validate mandatory fields on change
    if (name === 'name' || name === 'type' || name === 'discountValue' ||
        name === 'freeGiftDescription' || name === 'pointsRequired' ||
        name === 'voucherAmount' || name === 'startDate' || name === 'endDate' ||
        name === 'targetingType') {
      validateField(name, newValue, updatedFormData);
    }
  };
  
  const handleTargetingCriteriaChange = (field, value) => {
    setFormData({
      ...formData,
      targetingCriteria: {
        ...formData.targetingCriteria,
        [field]: value
      }
    });
    // NEW: Mark as unsaved when criteria changes
    setTargetingCriteriaSaved(false);
    setPreviewCompleted(false);
  };
  
  const handlePostalCodesChange = (e) => {
    const codes = e.target.value.split(',').map(code => code.trim()).filter(code => code);
    handleTargetingCriteriaChange('postalCodes', codes);
  };

  const handleBirthMonthsChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    handleTargetingCriteriaChange('birthMonths', selectedOptions);
  };

  const handleMemberTiersChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    handleTargetingCriteriaChange('memberTiers', selectedOptions);
  };
  
  const generateVoucherCode = () => {
    const chars = 'ABCDEFGHIJKLMNPQRSTUVWXYZ123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, voucherCode: result });
  };

  // NEW: Handler for saving targeting criteria
  const handleSaveTargetingCriteria = () => {
    setTargetingCriteriaSaved(true);
    setSuccessMessage('Targeting criteria saved successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };
// src/components/admin/PromotionsEditor.jsx
// PART 2 OF 2: Event Handlers and JSX Render
// This continues immediately after Part 1

  const handleAddPromotion = () => {
    setIsEditing(true);
    setEditingPromotionId(null);
    setSelectedCustomers([]);
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
    setTargetingCriteriaSaved(false); // NEW: Reset saved state
    setPreviewCompleted(false);
    setPreviewData(null);
    setFormData({
      name: '',
      description: '',
      type: 'percentage',
      discountValue: 0,
      freeGiftDescription: '',
      pointsRequired: 0,
      voucherAmount: 0,
      voucherCode: '',
      startDate: '',
      endDate: '',
      targetingType: 'not_selected', // UPDATED: Default to not_selected
      targetingCriteria: {
        birthMonths: [],
        postalCodes: [],
        lastTransactionMonths: 0,
        transactionCount: 0,
        transactionType: 'not_selected', // UPDATED: Default to not_selected
        transactionAmountType: 'not_selected', // UPDATED: Default to not_selected
        transactionAmount: 0,
        memberTiers: [],
        pointsType: 'not_selected', // UPDATED: Default to not_selected
        pointsAmount: 0,
        specificCustomers: [],
        appointmentTimeValue: 0,
        appointmentTimeUnit: 'days',
        appointmentService: 'not_selected',
        appointmentStaff: 'not_selected',
        appointmentStatus: []
      },
      isActive: true,
      maxRedemptions: null,
      currentRedemptions: 0
    });
    setFieldErrors({
      name: '',
      type: '',
      discountValue: '',
      freeGiftDescription: '',
      pointsRequired: '',
      voucherAmount: '',
      startDate: '',
      endDate: '',
      targetingType: ''
    });
    setError('');
  };
  
  const handleEditPromotion = (promotion) => {
    setIsEditing(true);
    setEditingPromotionId(promotion.id);
    setSelectedCustomers(promotion.targetingCriteria?.specificCustomers || []);
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
    setTargetingCriteriaSaved(true); // NEW: Editing existing promotion, criteria already saved
    setPreviewCompleted(false); // Must preview again before pushing vouchers
    setPreviewData(null);
    setFormData({
      ...promotion,
      startDate: (() => {
        try {
          return promotion.startDate ? new Date(promotion.startDate.seconds * 1000).toISOString().split('T')[0] : '';
        } catch (e) {
          return '';
        }
      })(),
      endDate: (() => {
        try {
          return promotion.endDate ? new Date(promotion.endDate.seconds * 1000).toISOString().split('T')[0] : '';
        } catch (e) {
          return '';
        }
      })(),
      targetingCriteria: {
        birthMonths: promotion.targetingCriteria?.birthMonths || [],
        postalCodes: promotion.targetingCriteria?.postalCodes || [],
        lastTransactionMonths: promotion.targetingCriteria?.lastTransactionMonths || 0,
        transactionCount: promotion.targetingCriteria?.transactionCount || 0,
        transactionType: promotion.targetingCriteria?.transactionType || 'not_selected',
        transactionAmountType: promotion.targetingCriteria?.transactionAmountType || 'not_selected',
        transactionAmount: promotion.targetingCriteria?.transactionAmount || 0,
        memberTiers: promotion.targetingCriteria?.memberTiers || [],
        pointsType: promotion.targetingCriteria?.pointsType || 'not_selected',
        pointsAmount: promotion.targetingCriteria?.pointsAmount || 0,
        specificCustomers: promotion.targetingCriteria?.specificCustomers || [],
        appointmentTimeValue: promotion.targetingCriteria?.appointmentTimeValue || 0,
        appointmentTimeUnit: promotion.targetingCriteria?.appointmentTimeUnit || 'days',
        appointmentService: promotion.targetingCriteria?.appointmentService || 'not_selected',
        appointmentStaff: promotion.targetingCriteria?.appointmentStaff || 'not_selected',
        appointmentStatus: promotion.targetingCriteria?.appointmentStatus || []
      }
    });
    setFieldErrors({
      name: '',
      type: '',
      discountValue: '',
      freeGiftDescription: '',
      pointsRequired: '',
      voucherAmount: '',
      startDate: '',
      endDate: '',
      targetingType: ''
    });
    setError('');
  };

  const handlePreviewTargeting = async () => {
    try {
      setPreviewLoading(true);
      setError('');
      const preview = await getCustomerTargetingPreview(salonId, {
        targetingType: formData.targetingType,
        targetingCriteria: formData.targetingCriteria
      });
      setPreviewData(preview);
      setShowPreviewModal(true);
      setPreviewCompleted(true);
    } catch (err) {
      console.error('Error getting preview:', err);
      setError('Failed to get targeting preview. Please try again.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePushVoucherToCustomers = async () => {
    // Validate conditions
    if (!targetingCriteriaSaved) {
      setError('Please save targeting criteria first.');
      setTimeout(() => setError(''), 5000);
      return;
    }
    if (!previewCompleted || !previewData) {
      setError('Please preview target customers first.');
      setTimeout(() => setError(''), 5000);
      return;
    }
    if (!formData.voucherCode || formData.voucherCode.trim() === '') {
      setError('Please create a voucher code first before pushing vouchers.');
      setTimeout(() => setError(''), 5000);
      return;
    }
    if (!previewData.eligibleCustomers || previewData.eligibleCustomers.length === 0) {
      setError('No eligible customers found. Please review your targeting criteria.');
      setTimeout(() => setError(''), 5000);
      return;
    }

    // Validate mandatory fields before saving
    const nameValid = validateField('name', formData.name);
    const startDateValid = validateField('startDate', formData.startDate);
    const endDateValid = validateField('endDate', formData.endDate);
    if (!nameValid || !startDateValid || !endDateValid) {
      setError('Please fill in all required promotion fields before pushing vouchers.');
      return;
    }

    try {
      setPushingVouchers(true);
      setError('');

      let promotionId = editingPromotionId;

      // Auto-save the promotion if it's new (no promotionId yet)
      if (!promotionId) {
        const promotionData = {
          ...formData,
          startDate: new Date(formData.startDate),
          endDate: new Date(formData.endDate),
          createdAt: new Date(),
          updatedAt: new Date()
        };
        promotionId = await addSalonPromotion(salonId, promotionData);
        setEditingPromotionId(promotionId);

        // Refresh promotions list
        const loadedPromotions = await getSalonPromotions(salonId);
        setPromotions(loadedPromotions);
      }

      // Prepare promotion data with proper date objects
      const promotionDataForVouchers = {
        ...formData,
        startDate: new Date(formData.startDate),
        endDate: new Date(formData.endDate)
      };

      // Push vouchers to customers
      const result = await pushVouchersToCustomers(
        salonId,
        promotionId,
        promotionDataForVouchers,
        previewData.eligibleCustomers
      );

      // Show success message
      let message = `Successfully pushed vouchers to ${result.created} customer${result.created !== 1 ? 's' : ''}!`;
      if (result.skipped > 0) {
        message += ` (${result.skipped} customer${result.skipped !== 1 ? 's' : ''} already had vouchers and were skipped)`;
      }
      if (result.removed > 0) {
        message += ` ${result.removed} voucher${result.removed !== 1 ? 's' : ''} removed from customers no longer matching criteria.`;
      }
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(''), 7000);

      // Refresh promotions list to show updated vouchersDistributed count
      const updatedPromotions = await getSalonPromotions(salonId);
      setPromotions(updatedPromotions);

    } catch (err) {
      console.error('Error pushing vouchers to customers:', err);
      setError(`Failed to push vouchers: ${err.message}. Please try again.`);
      setTimeout(() => setError(''), 7000);
    } finally {
      setPushingVouchers(false);
    }
  };

  const handleSavePromotion = async () => {
    // Validate all mandatory fields
    const nameValid = validateField('name', formData.name);
    const typeValid = validateField('type', formData.type);
    const startDateValid = validateField('startDate', formData.startDate);
    const endDateValid = validateField('endDate', formData.endDate);
    const targetingTypeValid = validateField('targetingType', formData.targetingType);

    // Conditional validation based on promotion type
    let discountValid = true;
    let freeGiftValid = true;
    let pointsRequiredValid = true;
    let voucherAmountValid = true;

    if (formData.type === 'percentage' || formData.type === 'fixed_amount') {
      discountValid = validateField('discountValue', formData.discountValue);
    }
    if (formData.type === 'free_gift') {
      freeGiftValid = validateField('freeGiftDescription', formData.freeGiftDescription);
    }
    if (formData.type === 'points_based') {
      pointsRequiredValid = validateField('pointsRequired', formData.pointsRequired);
      voucherAmountValid = validateField('voucherAmount', formData.voucherAmount);
    }

    if (!nameValid || !typeValid || !discountValid || !freeGiftValid ||
        !pointsRequiredValid || !voucherAmountValid || !startDateValid ||
        !endDateValid || !targetingTypeValid) {
      setError('Please fix the validation errors before saving.');
      return;
    }

    // Additional validation for date range
    if (new Date(formData.startDate) >= new Date(formData.endDate)) {
      setError('End date must be after start date');
      return;
    }

    // Additional validation for specific customer targeting
    if (formData.targetingType === 'specific_customer' && formData.targetingCriteria.specificCustomers.length === 0) {
      setError('Please select at least one customer for specific customer targeting');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const promotionData = {
        ...formData,
        startDate: new Date(formData.startDate),
        endDate: new Date(formData.endDate),
        updatedAt: new Date(),
        ...(editingPromotionId ? {} : { createdAt: new Date() })
      };
      if (editingPromotionId) {
        await updateSalonPromotion(salonId, editingPromotionId, promotionData);
        setPromotions(promotions.map(promo => 
          promo.id === editingPromotionId ? { ...promotionData, id: editingPromotionId } : promo
        ));
      } else {
        const newPromotionId = await addSalonPromotion(salonId, promotionData);
        const createdPromotion = await getSalonPromotions(salonId).then(promos =>
          promos.find(p => p.id === newPromotionId)
        );
        setPromotions([...promotions, createdPromotion || { ...promotionData, id: newPromotionId }]);
        setSuccessMessage('Promotion created successfully! Use "Push Voucher to Customers" to distribute vouchers.');
        setTimeout(() => setSuccessMessage(''), 5000);
      }
      setIsEditing(false);
      setEditingPromotionId(null);
      setSelectedCustomers([]);
      setCustomerSearchQuery('');
      setCustomerSearchResults([]);
      setTargetingCriteriaSaved(false); // NEW: Reset saved state
      setPreviewCompleted(false);
      setPreviewData(null);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error saving promotion:', err);
      setError('Failed to save promotion. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeletePromotion = async (promotionId) => {
    if (window.confirm('Are you sure you want to delete this promotion?')) {
      try {
        setSaving(true);
        await deleteSalonPromotion(salonId, promotionId);
        setPromotions(promotions.filter(promo => promo.id !== promotionId));
        if (onUpdate) onUpdate();
      } catch (err) {
        console.error('Error deleting promotion:', err);
        setError('Failed to delete promotion. Please try again.');
      } finally {
        setSaving(false);
      }
    }
  };
  
  const handleToggleActive = async (promotionId) => {
    try {
      const promotion = promotions.find(p => p.id === promotionId);
      if (!promotion) return;
      const updatedPromotion = { ...promotion, isActive: !promotion.isActive };
      await updateSalonPromotion(salonId, promotionId, { isActive: updatedPromotion.isActive });
      setPromotions(promotions.map(p => p.id === promotionId ? updatedPromotion : p));
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error toggling promotion status:', err);
      setError('Failed to update promotion status. Please try again.');
    }
  };
  
  const handleBulkAction = async (action) => {
    if (selectedPromotions.length === 0) {
      setError('Please select promotions to perform bulk action');
      return;
    }
    try {
      setSaving(true);
      for (const promotionId of selectedPromotions) {
        if (action === 'activate') {
          await updateSalonPromotion(salonId, promotionId, { isActive: true });
        } else if (action === 'deactivate') {
          await updateSalonPromotion(salonId, promotionId, { isActive: false });
        }
      }
      setPromotions(promotions.map(promo => 
        selectedPromotions.includes(promo.id) ? { ...promo, isActive: action === 'activate' } : promo
      ));
      setSelectedPromotions([]);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error performing bulk action:', err);
      setError('Failed to perform bulk action. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  const handleSelectPromotion = (promotionId) => {
    setSelectedPromotions(prev => 
      prev.includes(promotionId) ? prev.filter(id => id !== promotionId) : [...prev, promotionId]
    );
  };
  
  const handleSelectAll = () => {
    if (selectedPromotions.length === promotions.length) {
      setSelectedPromotions([]);
    } else {
      setSelectedPromotions(promotions.map(p => p.id));
    }
  };
  
  const getPromotionStatus = (promotion) => {
    const now = new Date();
    const startDate = promotion.startDate?.seconds ? new Date(promotion.startDate.seconds * 1000) : new Date(promotion.startDate);
    const endDate = promotion.endDate?.seconds ? new Date(promotion.endDate.seconds * 1000) : new Date(promotion.endDate);
    if (!promotion.isActive) return 'inactive';
    if (now < startDate) return 'scheduled';
    if (now > endDate) return 'expired';
    return 'active';
  };
  
  const formatDate = (date) => {
    if (!date) return '';
    const d = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
    return d.toLocaleDateString();
  };
  
  if (loading && promotions.length === 0) {
    return <div className="loading">Loading promotions...</div>;
  }
  
  return (
    <div className="promotions-editor">
      <h2>Promotions</h2>
      
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}
      
      {previewLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <div className="loading-text">Loading customer preview...</div>
          </div>
        </div>
      )}
      
      <CustomerPreviewModal 
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        previewData={previewData}
        promotionName={formData.name || 'promotion'}
      />
      
      {!isEditing ? (
        <>
          <div className="promotions-actions">
            <button className="add-button" onClick={handleAddPromotion}>
              Add New Promotion
            </button>
            {selectedPromotions.length > 0 && (
              <div className="bulk-actions">
                <button 
                  className="bulk-button"
                  onClick={() => handleBulkAction('activate')}
                  disabled={saving}
                >
                  Activate Selected
                </button>
                <button 
                  className="bulk-button"
                  onClick={() => handleBulkAction('deactivate')}
                  disabled={saving}
                >
                  Deactivate Selected
                </button>
              </div>
            )}
          </div>
          
          {promotions.length === 0 ? (
            <div className="no-promotions">
              <p>No promotions have been created yet. Click "Add New Promotion" to get started.</p>
            </div>
          ) : (
            <>
              <div className="select-all-section">
                <input
                  type="checkbox"
                  checked={selectedPromotions.length === promotions.length && promotions.length > 0}
                  onChange={handleSelectAll}
                />
                <span>Select All</span>
              </div>
              
              <div className="promotions-list">
                {promotions.map(promotion => (
                  <div 
                    key={promotion.id} 
                    className={`promotion-item ${getPromotionStatus(promotion)}`}
                  >
                    <div className="promotion-select">
                      <input
                        type="checkbox"
                        checked={selectedPromotions.includes(promotion.id)}
                        onChange={() => handleSelectPromotion(promotion.id)}
                      />
                    </div>
                    
                    <div className="promotion-content">
                      <div className="promotion-header">
                        <h4>{promotion.name}</h4>
                        <div className="promotion-badges">
                          <span className={`status-badge ${getPromotionStatus(promotion)}`}>
                            {getPromotionStatus(promotion)}
                          </span>
                          <span className="type-badge">
                            {promotion.type === 'percentage' && `${promotion.discountValue}% OFF`}
                            {promotion.type === 'fixed_amount' && `$${promotion.discountValue} OFF`}
                            {promotion.type === 'free_gift' && 'FREE GIFT'}
                            {promotion.type === 'points_based' && `${promotion.pointsRequired} pts = $${promotion.voucherAmount}`}
                          </span>
                        </div>
                      </div>
                      <p className="promotion-description">{promotion.description}</p>
                      <div className="promotion-details">
                        <div className="detail-item">
                          <strong>Code:</strong> {promotion.voucherCode}
                        </div>
                        <div className="detail-item">
                          <strong>Valid:</strong> {formatDate(promotion.startDate)} - {formatDate(promotion.endDate)}
                        </div>
                        <div className="detail-item">
                          <strong>Targeting:</strong> {promotion.targetingType.replace('_', ' ')}
                        </div>
                        <div className="detail-item">
                          <strong>Redemptions:</strong> 
                          {promotion.currentRedemptions || 0}
                          {promotion.maxRedemptions ? ` / ${promotion.maxRedemptions}` : ' / Unlimited'}
                        </div>
                      </div>
                    </div>
                    
                    <div className="promotion-actions">
                      <button 
                        className="edit-button" 
                        onClick={() => handleEditPromotion(promotion)}
                      >
                        Edit
                      </button>
                      <button 
                        className={promotion.isActive ? "remove-button" : "add-button"}
                        onClick={() => handleToggleActive(promotion.id)}
                      >
                        {promotion.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button 
                        className="remove-button"
                        onClick={() => handleDeletePromotion(promotion.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <div className="promotion-form">
          <h3>{editingPromotionId ? 'Edit Promotion' : 'Create New Promotion'}</h3>
          
          <div className="form-section">
            <h4>Basic Information</h4>
            <div className="form-group">
              <label htmlFor="name">
                Promotion Name<span className="required-asterisk">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                className={fieldErrors.name ? 'input-error' : ''}
                placeholder="e.g., Summer Special Offer"
                required
              />
              {fieldErrors.name && <span className="field-error-message">{fieldErrors.name}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                placeholder="Describe your promotion..."
              />
            </div>
          </div>
          
          <div className="form-section">
            <h4>Promotion Type</h4>
            <div className="form-group">
              <label htmlFor="type">
                Type<span className="required-asterisk">*</span>
              </label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                className={fieldErrors.type ? 'input-error' : ''}
              >
                <option value="percentage">Percentage Discount</option>
                <option value="fixed_amount">Fixed Amount Off</option>
                <option value="free_gift">Free Gift with Purchase</option>
                <option value="points_based">Points Based Redemption</option>
              </select>
              {fieldErrors.type && <span className="field-error-message">{fieldErrors.type}</span>}
            </div>
            {(formData.type === 'percentage' || formData.type === 'fixed_amount') && (
              <div className="form-group">
                <label htmlFor="discountValue">
                  {formData.type === 'percentage' ? 'Discount Percentage' : 'Discount Amount ($)'}
                  <span className="required-asterisk">*</span>
                </label>
                <input
                  type="number"
                  id="discountValue"
                  name="discountValue"
                  value={formData.discountValue || ''}
                  onChange={handleInputChange}
                  onBlur={handleFieldBlur}
                  className={fieldErrors.discountValue ? 'input-error' : ''}
                  min="0"
                  max={formData.type === 'percentage' ? "100" : undefined}
                  step={formData.type === 'percentage' ? "1" : "0.01"}
                  required
                />
                {fieldErrors.discountValue && <span className="field-error-message">{fieldErrors.discountValue}</span>}
              </div>
            )}
            {formData.type === 'free_gift' && (
              <div className="form-group">
                <label htmlFor="freeGiftDescription">
                  Free Gift Description<span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  id="freeGiftDescription"
                  name="freeGiftDescription"
                  value={formData.freeGiftDescription}
                  onChange={handleInputChange}
                  onBlur={handleFieldBlur}
                  className={fieldErrors.freeGiftDescription ? 'input-error' : ''}
                  placeholder="e.g., Free Hair Ampoule with Hair Colour Service"
                  required
                />
                {fieldErrors.freeGiftDescription && <span className="field-error-message">{fieldErrors.freeGiftDescription}</span>}
              </div>
            )}
            {formData.type === 'points_based' && (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="pointsRequired">
                    Points Required<span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="number"
                    id="pointsRequired"
                    name="pointsRequired"
                    value={formData.pointsRequired || ''}
                    onChange={handleInputChange}
                    onBlur={handleFieldBlur}
                    className={fieldErrors.pointsRequired ? 'input-error' : ''}
                    min="1"
                    step="1"
                    placeholder="e.g., 1000"
                    required
                  />
                  {fieldErrors.pointsRequired && <span className="field-error-message">{fieldErrors.pointsRequired}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="voucherAmount">
                    Voucher Amount ($)<span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="number"
                    id="voucherAmount"
                    name="voucherAmount"
                    value={formData.voucherAmount || ''}
                    onChange={handleInputChange}
                    onBlur={handleFieldBlur}
                    className={fieldErrors.voucherAmount ? 'input-error' : ''}
                    min="0.01"
                    step="0.01"
                    placeholder="e.g., 10.00"
                    required
                  />
                  {fieldErrors.voucherAmount && <span className="field-error-message">{fieldErrors.voucherAmount}</span>}
                </div>
              </div>
            )}
          </div>
          
          <div className="form-section">
            <h4>Voucher Code</h4>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="voucherCode">Voucher Code*</label>
                <input
                  type="text"
                  id="voucherCode"
                  name="voucherCode"
                  value={formData.voucherCode}
                  onChange={handleInputChange}
                  maxLength="10"
                  placeholder="Enter code or generate"
                  required
                />
              </div>
              <button 
                type="button" 
                className="generate-button"
                onClick={generateVoucherCode}
              >
                Generate Code
              </button>
            </div>
          </div>
          
          <div className="form-section">
            <h4>Validity Period</h4>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="startDate">
                  Start Date<span className="required-asterisk">*</span>
                </label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  onBlur={handleFieldBlur}
                  className={fieldErrors.startDate ? 'input-error' : ''}
                  required
                />
                {fieldErrors.startDate && <span className="field-error-message">{fieldErrors.startDate}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="endDate">
                  End Date<span className="required-asterisk">*</span>
                </label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  onBlur={handleFieldBlur}
                  className={fieldErrors.endDate ? 'input-error' : ''}
                  required
                />
                {fieldErrors.endDate && <span className="field-error-message">{fieldErrors.endDate}</span>}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h4>Customer Targeting</h4>
            <div className="form-group">
              <label htmlFor="targetingType">
                Target Audience<span className="required-asterisk">*</span>
              </label>
              <select
                id="targetingType"
                name="targetingType"
                value={formData.targetingType}
                onChange={handleInputChange}
                className={fieldErrors.targetingType ? 'input-error' : ''}
              >
                <option value="not_selected">Not Selected</option>
                <option value="all_customers">All Customers</option>
                <option value="customer_profile">Customer Profile Based</option>
                <option value="customer_transaction">Customer Transaction Based</option>
                <option value="customer_points">Customer Points Based</option>
                <option value="customer_appointment">Customer Appointment Based</option>
                <option value="specific_customer">Specific Customer</option>
                <option value="customer_notes">Customer Notes Based</option>
              </select>
              {fieldErrors.targetingType && <span className="field-error-message">{fieldErrors.targetingType}</span>}
            </div>
            
            {formData.targetingType === 'customer_profile' && (
              <div className="targeting-criteria">
                <div className="form-group">
                  <label>Birth Months</label>
                  <select multiple value={formData.targetingCriteria.birthMonths} onChange={handleBirthMonthsChange} size="6">
                    {birthMonthOptions.map(month => (
                      <option key={month} value={month}>{month}</option>
                    ))}
                  </select>
                  <div className="form-helper-text">Hold Ctrl/Cmd to select multiple months</div>
                </div>
                <div className="form-group">
                  <label>Postal Codes (comma-separated)</label>
                  <input
                    type="text"
                    value={formData.targetingCriteria.postalCodes.join(', ')}
                    onChange={handlePostalCodesChange}
                    placeholder="e.g., 560, 123456, 567890"
                  />
                  <div className="form-helper-text">
                    Leave empty for all postal codes. Use partial codes for broader targeting (e.g., "560" matches all codes containing "560")
                  </div>
                </div>
              </div>
            )}
            
            {formData.targetingType === 'customer_transaction' && (
              <div className="targeting-criteria">
                {isMultiLocation && (
                  <div className="form-group">
                    <label>Location Name</label>
                    <select
                      value={formData.targetingCriteria.transactionLocationId || 'not_selected'}
                      onChange={(e) => handleTargetingCriteriaChange('transactionLocationId', e.target.value)}
                    >
                      <option value="not_selected">All Locations</option>
                      {locations.map(loc => (
                        <option key={loc.locationId} value={loc.locationId}>
                          {loc.locationName}{loc.isHeadOffice ? ' (Head Office)' : ''}
                        </option>
                      ))}
                    </select>
                    <div className="form-helper-text">
                      Filter by transactions at a specific location, or leave as "All Locations".
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label>Transaction Count Criteria</label>
                  <select value={formData.targetingCriteria.transactionType} onChange={(e) => handleTargetingCriteriaChange('transactionType', e.target.value)}>
                    <option value="not_selected">Not Selected</option>
                    <option value="less_than">Less than X transactions</option>
                    <option value="equal_to">Exactly X transactions</option>
                    <option value="greater_than">More than X transactions</option>
                  </select>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Number of Transactions</label>
                    <input type="number" value={formData.targetingCriteria.transactionCount} onChange={(e) => handleTargetingCriteriaChange('transactionCount', Number(e.target.value))} min="0" />
                  </div>
                  <div className="form-group">
                    <label>Last Transaction (months ago)</label>
                    <input type="number" value={formData.targetingCriteria.lastTransactionMonths} onChange={(e) => handleTargetingCriteriaChange('lastTransactionMonths', Number(e.target.value))} min="0" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Spend Amount Criteria</label>
                  <select value={formData.targetingCriteria.transactionAmountType} onChange={(e) => handleTargetingCriteriaChange('transactionAmountType', e.target.value)}>
                    <option value="not_selected">Not Selected</option>
                    <option value="greater_than">Greater Than the Following Amount</option>
                    <option value="less_than">Less Than the Following Amount</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Amount ($)</label>
                  <input type="number" value={formData.targetingCriteria.transactionAmount} onChange={(e) => handleTargetingCriteriaChange('transactionAmount', Number(e.target.value))} min="0" step="0.01" placeholder="0.00" />
                </div>
              </div>
            )}

            {formData.targetingType === 'customer_points' && (
              <div className="targeting-criteria">
                <div className="form-group">
                  <label>Member Tiers</label>
                  <select multiple value={formData.targetingCriteria.memberTiers} onChange={handleMemberTiersChange} size="5">
                    {memberTierOptions.map(tier => (
                      <option key={tier} value={tier}>{tier}</option>
                    ))}
                  </select>
                  <div className="form-helper-text">Hold Ctrl/Cmd to select multiple tiers</div>
                </div>
                <div className="form-group">
                  <label>Points Criteria</label>
                  <select value={formData.targetingCriteria.pointsType} onChange={(e) => handleTargetingCriteriaChange('pointsType', e.target.value)}>
                    <option value="not_selected">Not Selected</option>
                    <option value="greater_than">Greater Than the Following Number of Points</option>
                    <option value="less_than">Less Than the Following Number of Points</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Number of Points</label>
                  <input type="number" value={formData.targetingCriteria.pointsAmount} onChange={(e) => handleTargetingCriteriaChange('pointsAmount', Number(e.target.value))} min="0" placeholder="0" />
                </div>
              </div>
            )}

            {formData.targetingType === 'customer_appointment' && (
              <div className="targeting-criteria">
                <div className="form-group">
                  <label>Upcoming Request within</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="number"
                      value={formData.targetingCriteria.appointmentTimeValue}
                      onChange={(e) => handleTargetingCriteriaChange('appointmentTimeValue', Number(e.target.value))}
                      min="0"
                      style={{ width: '80px' }}
                    />
                    <select
                      value={formData.targetingCriteria.appointmentTimeUnit}
                      onChange={(e) => handleTargetingCriteriaChange('appointmentTimeUnit', e.target.value)}
                    >
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                      <option value="months">Months</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Service Requested</label>
                  <select
                    value={formData.targetingCriteria.appointmentService}
                    onChange={(e) => handleTargetingCriteriaChange('appointmentService', e.target.value)}
                    disabled={loadingServicesStaff}
                  >
                    <option value="not_selected">Not Selected (Any Service)</option>
                    {salonServices.map(service => (
                      <option key={service.id} value={service.id}>{service.name}</option>
                    ))}
                  </select>
                  {loadingServicesStaff && <span className="form-helper-text">Loading services...</span>}
                </div>

                <div className="form-group">
                  <label>Staff Requested</label>
                  <select
                    value={formData.targetingCriteria.appointmentStaff}
                    onChange={(e) => handleTargetingCriteriaChange('appointmentStaff', e.target.value)}
                    disabled={loadingServicesStaff}
                  >
                    <option value="not_selected">Not Selected (Any Staff)</option>
                    {salonStaff.map(staff => (
                      <option key={staff.id} value={staff.id}>{staff.name}</option>
                    ))}
                  </select>
                  {loadingServicesStaff && <span className="form-helper-text">Loading staff...</span>}
                </div>

                <div className="form-group">
                  <label>Appointment Status</label>
                  <select
                    multiple
                    value={formData.targetingCriteria.appointmentStatus}
                    onChange={(e) => {
                      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                      handleTargetingCriteriaChange('appointmentStatus', selectedOptions);
                    }}
                    size="4"
                  >
                    {appointmentStatusOptions.map(status => (
                      <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                    ))}
                  </select>
                  <div className="form-helper-text">
                    Hold Ctrl/Cmd to select multiple statuses. Leave empty for any status.
                  </div>
                </div>
              </div>
            )}

            {formData.targetingType === 'specific_customer' && (
              <div className="targeting-criteria">
                <div className="form-group">
                  <label>Search and Select Customers</label>
                  <div className="customer-search-container">
                    <input type="text" value={customerSearchQuery} onChange={handleCustomerSearchChange} onFocus={() => setShowCustomerDropdown(true)} placeholder="Search by customer name or email..." className="customer-search-input" />
                    {showCustomerDropdown && (
                      <div className="customer-search-dropdown">
                        {searchingCustomers && <div className="customer-search-loading">Searching...</div>}
                        {!searchingCustomers && customerSearchResults.length === 0 && customerSearchQuery.length >= 2 && <div className="customer-search-no-results">No customers found</div>}
                        {!searchingCustomers && customerSearchResults.length > 0 && (
                          <>
                            {customerSearchResults.map(customer => (
                              <div key={customer.id} className="customer-search-result" onClick={() => handleCustomerSelect(customer)}>
                                <div className="customer-result-name">{customer.displayName}</div>
                                <div className="customer-result-email">{customer.email}</div>
                                {customer.phoneNumber && <div className="customer-result-phone">{customer.phoneNumber}</div>}
                              </div>
                            ))}
                          </>
                        )}
                        {customerSearchQuery.length < 2 && <div className="customer-search-hint">Type at least 2 characters to search</div>}
                      </div>
                    )}
                  </div>
                  {selectedCustomers.length > 0 && (
                    <div className="selected-customers">
                      <h5>Selected Customers ({selectedCustomers.length})</h5>
                      <div className="selected-customers-list">
                        {selectedCustomers.map(customer => (
                          <div key={customer.id} className="selected-customer-item">
                            <div className="selected-customer-info">
                              <div className="selected-customer-name">{customer.displayName}</div>
                              <div className="selected-customer-email">{customer.email}</div>
                            </div>
                            <button type="button" className="remove-customer-button" onClick={() => handleCustomerRemove(customer.id)}>Remove</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* #Revised UI_v2 — customer notes targeting */}
            {formData.targetingType === 'customer_notes' && (
              <div className="targeting-criteria">
                <div className="form-group">
                  <label>Search Terms</label>
                  <input
                    type="text"
                    value={formData.targetingCriteria.notesSearchText || ''}
                    onChange={(e) => handleTargetingCriteriaChange('notesSearchText', e.target.value)}
                    placeholder="Enter words or phrase to search in customer notes..."
                  />
                  <div className="form-helper-text">Customers whose notes match the search terms will be targeted</div>
                </div>
                <div className="form-group">
                  <label>Match Type</label>
                  <select
                    value={formData.targetingCriteria.notesMatchType || 'contains_all'}
                    onChange={(e) => handleTargetingCriteriaChange('notesMatchType', e.target.value)}
                  >
                    <option value="exact">Exact Match — notes contain this exact phrase</option>
                    <option value="contains_any">Contains Any Words — notes contain at least one of the words</option>
                    <option value="contains_all">Contains All Words — notes contain all words (any order)</option>
                  </select>
                </div>
              </div>
            )}

            <div className="targeting-buttons">
              <button
                type="button"
                className="save-criteria-button"
                onClick={handleSaveTargetingCriteria}
                disabled={formData.targetingType === 'not_selected'}
              >
                Save Targeting Criteria
              </button>
              <button
                type="button"
                className="preview-button"
                onClick={handlePreviewTargeting}
                disabled={previewLoading || !targetingCriteriaSaved}
                style={{ opacity: targetingCriteriaSaved ? 1 : 0.5 }}
              >
                {previewLoading ? 'Loading...' : 'Preview Target Customers'}
              </button>
              <div className="push-voucher-container">
                <button
                  type="button"
                  className="push-voucher-button"
                  onClick={handlePushVoucherToCustomers}
                  disabled={
                    pushingVouchers ||
                    !targetingCriteriaSaved ||
                    !previewCompleted ||
                    !formData.voucherCode ||
                    formData.voucherCode.trim() === ''
                  }
                >
                  {pushingVouchers ? 'Pushing Vouchers...' : 'Push Voucher to Customers'}
                </button>
                {(!formData.voucherCode || formData.voucherCode.trim() === '') && targetingCriteriaSaved && previewCompleted && (
                  <span className="push-voucher-hint">Please create a voucher code first</span>
                )}
                {!targetingCriteriaSaved && (
                  <span className="push-voucher-hint">Save targeting criteria first</span>
                )}
                {targetingCriteriaSaved && !previewCompleted && formData.voucherCode && (
                  <span className="push-voucher-hint">Preview target customers first</span>
                )}
              </div>
              <button
                type="button"
                className="preview-button campaign-button"
                onClick={handleCreateEmailCampaign}
                disabled={!formData.name}
              >
                Create Email Campaign
              </button>
              <button 
                type="button" 
                className="preview-button campaign-button" 
                onClick={handleCreatePushCampaign} 
                disabled={!formData.name}
              >
                Create Push Campaign
              </button>
            </div>
          </div>
          
          <div className="form-section">
            <h4>Additional Settings</h4>
            <div className="form-group">
              <label htmlFor="maxRedemptions">Maximum Redemptions</label>
              <input type="number" id="maxRedemptions" name="maxRedemptions" value={formData.maxRedemptions || ''} onChange={handleInputChange} min="1" placeholder="Leave empty for unlimited" />
              <div className="form-helper-text">Leave empty for unlimited redemptions</div>
            </div>
            <div className="form-group checkbox-group">
              <input type="checkbox" id="isActive" name="isActive" checked={formData.isActive} onChange={handleInputChange} />
              <label htmlFor="isActive">Promotion is active</label>
            </div>
          </div>
          
          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={() => {
              setIsEditing(false);
              setEditingPromotionId(null);
              setSelectedCustomers([]);
              setCustomerSearchQuery('');
              setCustomerSearchResults([]);
              setTargetingCriteriaSaved(false);
              setPreviewCompleted(false);
              setPreviewData(null);
            }}>
              Cancel
            </button>
            <button type="button" className="save-button" onClick={handleSavePromotion} disabled={saving}>
              {saving ? 'Saving...' : editingPromotionId ? 'Update Promotion' : 'Create Promotion'}
            </button>
          </div>
          <div className="promotion-note" style={{
            marginTop: '16px',
            padding: '12px 16px',
            backgroundColor: '#fff8e1',
            border: '1px solid #ffe082',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#6d4c00',
            lineHeight: '1.5'
          }}>
            <strong>Note:</strong> When the parameters of a promotion are changed and &quot;Update Promotion&quot; is clicked, the values will change for all targeted customers. This also applies when the targeting criteria is changed. Customers that were previously targeted and no longer fulfil the current conditions will no longer be assigned the vouchers, except for those who have redeemed the vouchers.
          </div>
        </div>
      )}
    </div>
  );
}

export default PromotionsEditor;